import { callApi } from "@src/utils/api-client";
import ClientLogsService from "./client-logs.service";
import { buildScreenerAnswersConfig } from "./configs";
import { runManagedRequest } from "./request-manager";

export interface ScreenerAnswer {
  question_id: number;
  response: "Yes" | "No";
}

export interface ScreenerAnswersPayload {
  answers: ScreenerAnswer[];
}

export interface ScreenerAnswersResponse {
  message?: string;
  error?: string;
}

/**
 * Posts screener answers for a given user to the backend.
 * Uses single-flight deduplication: if a submission for the same user is already
 * in-flight, the second caller joins the first rather than sending a duplicate request.
 */
export const postScreenerAnswers = async (
  userUuid: string,
  answers: ScreenerAnswer[],
): Promise<ScreenerAnswersResponse> => {
  return runManagedRequest({
    key: `screener:${userUuid}`,
    policy: "single-flight",
    request: async (signal) => {
      try {
        const { data } = await callApi(buildScreenerAnswersConfig(userUuid, answers, signal));
        return (data ?? {}) as ScreenerAnswersResponse;
      } catch (error) {
        ClientLogsService.logError(
          userUuid,
          "screener-answers.service.postScreenerAnswers",
          "Failed to post screener answers",
          error instanceof Error ? error : new Error(String(error)),
          {
            errorType: "DependencyFailure",
            dependencyName: "ScreenerAnswersAPI",
            functionName: "postScreenerAnswers",
          },
        );
        return { error: "Failed to post screener answers" };
      }
    },
  });
};
